/* eslint-disable react-hooks/exhaustive-deps */
import { AnonymousAppConfigurationProvider } from '@fmm/react-app-configuration';
import { LogLevel } from '@microsoft/signalr';
import Axios from 'axios';
import LogRocket from 'logrocket';
import React, {
  lazy,
  Suspense,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { setApplicationError } from 'src/actions';
import 'src/App.css';
import { CinchAppConfigProvider, useAuth0 } from 'src/applicationproviders';
import { AuthenticationGate } from 'src/components/baseComponents/AuthenticationGate';
import FullPageErrorBoundary from 'src/components/baseComponents/FullPageErrorBoundary';
import { Header } from 'src/components/baseComponents/Header';
import Loading from 'src/components/baseComponents/Loading';
import NotFound from 'src/components/baseComponents/NotFound';
import { SlowSuspense } from 'src/components/baseComponents/SlowSuspense';
import Modal from 'src/components/loanOfficer/Modals/Modal';
import LoLogin from 'src/components/login';
import Terms from 'src/components/login/termsandconditions';
import Privacy from 'src/components/login/privacy';
import MultifamilyPortal from 'src/components/MultifamilyPortal';
import env from 'src/env';
import history from 'src/history';
import { useCurrentLoanOfficer } from 'src/hooks';
import WebsocketProvider from 'src/hooks/signalr/WebsocketProvider';
import { useConfirmation } from 'src/hooks/use-confirmation';
import { ai } from 'src/TelemetryService';
import {
  apiAxios,
  CINCH_COC_AUTO_DISCLOSURES_FLAG,
  CINCH_COPY_LOAN_FLAG,
  CINCH_DU_LP_SERVICE_FLAG,
  CINCH_EXCLUDED_FIELDS,
  CINCH_INITIAL_DISCLOSURES_FLAG,
  CINCH_NQM_PRICER_FLAG,
  CINCH_NOI_FLAG,
  globalAxiosAbortController,
  CINCH_NEW_PREAPPROVAL,
  CINCH_CUSTOM_EMAIL_SIGNATURE,
  CINCH_SUMMARY_AND_TOOLS,
  CINCH_FRAUDGUARD,
  CINCH_ACCOUNTING_FLAG,
  CINCH_OPTIMALBLUE_SERVICE_FLAG,
  CINCH_STATEMENT_OF_DENIAL_FLAG,
  CINCH_SIGNNOW_SERVICE_FLAG,
  CINCH_CONSENT_SERVICE_FLAG,
  CINCH_USE_ECONSENT_FLAG,
  CINCH_FNF_RUN_ERNST,
  CINCH_CLOSING_DISCLOSURES_FLAG,
  CINCH_COPY_DOCUMENTS,
  CINCH_DRAW_REQUEST,
  CINCH_CLOSING_CALENDAR,
  CINCH_IMPERSONATION_FLAG,
} from 'src/util';
import CommercialExceptionApproval from './components/loanOfficer/CommercialExceptionApproval';
import { useInputFocusOnNavigation } from './hooks/use-focusonnavigation';
import { Logout } from 'src/components/login/logout';
import EllieMaeStatusBanner from 'src/components/baseComponents/Header/EllieMaeStatusBanner';
import { ImpersonationBanner } from 'src/components/baseComponents/ImpersonationBanner';
import ChatLauncher from './components/loanOfficer/AiChat/ChatLauncher';

const LOBase = lazy(() => import('src/components/loanOfficer'));
const AdminBase = lazy(() => import('src/components/admin'));
const ComplianceBase = lazy(() => import('src/components/compliance'));
const ServerPicker = lazy(
  () => import('src/components/development/ServerPicker'),
);
const EFolderPopOut = lazy(
  () => import('src/components/loanOfficer/Loan/EFolder/PopOut'),
);
const MultifamilyPopOut = lazy(
  () => import('src/components/loanOfficer/Loan/MultiFamilyDocuments/PopOut'),
);

const featureFlags = [
  CINCH_COC_AUTO_DISCLOSURES_FLAG,
  CINCH_INITIAL_DISCLOSURES_FLAG,
  CINCH_COPY_LOAN_FLAG,
  CINCH_NQM_PRICER_FLAG,
  CINCH_EXCLUDED_FIELDS,
  CINCH_DU_LP_SERVICE_FLAG,
  CINCH_NOI_FLAG,
  CINCH_NEW_PREAPPROVAL,
  CINCH_CUSTOM_EMAIL_SIGNATURE,
  CINCH_SUMMARY_AND_TOOLS,
  CINCH_FRAUDGUARD,
  CINCH_ACCOUNTING_FLAG,
  CINCH_OPTIMALBLUE_SERVICE_FLAG,
  CINCH_STATEMENT_OF_DENIAL_FLAG,
  CINCH_SIGNNOW_SERVICE_FLAG,
  CINCH_CONSENT_SERVICE_FLAG,
  CINCH_USE_ECONSENT_FLAG,
  CINCH_FNF_RUN_ERNST,
  CINCH_CLOSING_DISCLOSURES_FLAG,
  CINCH_COPY_DOCUMENTS,
  CINCH_DRAW_REQUEST,
  CINCH_CLOSING_CALENDAR,
  CINCH_IMPERSONATION_FLAG,
];

ai.initialize({ history: history });

// eslint-disable-next-line max-statements
const App = () => {
  const dispatch = useDispatch();
  const {
    apiServer,
    functionServer,
    httpError,
    httpErrorObj,
    activeEnvironment,
  } = useSelector((store) => store.app);
  const location = useLocation();
  const navigate = useNavigate();
  const [modal, setModal] = useState(null);
  const confirm = useConfirmation();
  const loanOfficer = useCurrentLoanOfficer();
  const callbackHandledRef = useRef(false);
  const [axiosHooksReady, setAxiosHooksReady] = useState({
    api: false,
    errors: false,
  });

  const { activate } = useIdleTimer({
    onPrompt: () => {
      if (authenticated && !modal) {
        toggleModal('IdleTimeout');
      }
    },
    onActive: () => setModal(null),
    debounce: 250,
    promptBeforeIdle: 1000 * 60 * 1,
    timeout: 1000 * 60 * 45, // millseconds * seconds * minutes
    crossTab: true,
  });

  const {
    ready: auth0Ready,
    handleRedirect,
    authenticated,
    getToken,
  } = useAuth0();
  const axiosReady = useMemo(
    () =>
      auth0Ready &&
      Object.keys(axiosHooksReady).length &&
      Object.values(axiosHooksReady).every(Boolean),
    [auth0Ready, axiosHooksReady],
  );

  useInputFocusOnNavigation();

  useEffect(() => {
    (async () => {
      // Only handle callback if we have OAuth params (code/state or error) and haven't handled it yet
      const hasOAuthParams =
        location.search.includes('code=') || location.search.includes('error=');

      if (
        location.pathname?.includes('/callback') &&
        hasOAuthParams &&
        !callbackHandledRef.current
      ) {
        // Wait for Auth0 to be ready before processing
        if (!auth0Ready) {
          return;
        }

        callbackHandledRef.current = true;
        try {
          const result = await handleRedirect();

          // Ensure auth state is updated by getting a token
          if (result && !location.search.includes('error')) {
            await getToken(false);
          }

          if (result || location.search.includes('error')) {
            const targetUrl = location.search.includes('error')
              ? '/login'
              : (result?.appState?.targetUrl ?? '/');
            navigate(targetUrl, { replace: true });
          } else {
            console.warn('No redirect result and no error, redirecting to /');
            navigate('/', { replace: true });
          }
        } catch (error) {
          console.error('Error in handleRedirect:', error);
          callbackHandledRef.current = false; // Allow retry on error
          navigate('/login', { replace: true });
        }
      } else if (location.pathname?.includes('/callback') && !hasOAuthParams) {
        // If on callback without OAuth params, redirect to home
        navigate('/', { replace: true });
      }
    })();
  }, [location.pathname, location.search, auth0Ready]);

  const handleAxiosError = (error) => {
    if (Axios.isCancel(error)) {
      return Promise.reject(error);
    }
    const resp = error.response;
    if (resp ? !resp.status.toString().startsWith('4') : true) {
      dispatch(
        setApplicationError(
          resp
            ? resp.data?.messages?.[0]
            : 'Something went wrong! Please check there are no outages. Also, try to log out and back in as well. Then please refresh and try again.',
          error,
        ),
      );
    }
    return Promise.reject(error);
  };

  useEffect(() => {
    apiAxios.interceptors.request.use((config) => {
      if (
        LogRocket.sessionURL &&
        !LogRocket.sessionURL.includes('LogRocket.init()')
      ) {
        config.headers['X-LogRocket-URL'] = LogRocket.sessionURL;
      }
      if (!config.signal) config.signal = globalAxiosAbortController.signal;
      return config;
    });

    Axios.interceptors.response.use(null, handleAxiosError);
    apiAxios.interceptors.response.use(null, handleAxiosError);
    setAxiosHooksReady((c) => ({ ...c, errors: true }));
    // this works for now, but avoid alert() after this
    window.alert = (str) => {
      return confirm({
        variant: 'alert',
        title: str.toString(),
      });
    };
  }, []);

  useEffect(() => {
    Axios.defaults.baseURL = functionServer;
    apiAxios.defaults.baseURL = apiServer;
    setAxiosHooksReady((c) => ({ ...c, api: true }));
  }, [functionServer, apiServer]);

  useEffect(() => {
    (async () => {
      if (httpError) {
        await confirm({
          variant: 'alert',
          description: (
            <div>
              <p>
                {httpError ||
                  'Something went wrong! Please refresh and try again.'}
              </p>
              {env.DEPLOY_ENV === 'development' && (
                <pre>{JSON.stringify(httpErrorObj, null, 2)}</pre>
              )}
            </div>
          ),
        });
        setApplicationError('', null);
      }
    })();
  }, [httpError]);

  const wsHubOptions = useMemo(
    () => ({
      logMessageContent: process.env.NODE_ENV === 'development',
      logger:
        process.env.NODE_ENV === 'development'
          ? LogLevel.Warning
          : LogLevel.Error,
      accessTokenFactory: getToken,
    }),
    [getToken],
  );

  const toggleModal = (modalName = null) => {
    setModal(modalName);
    if (!modalName) {
      activate();
    }
  };

  return (
    <FullPageErrorBoundary>
      <Suspense fallback={<Loading />}>
        <AnonymousAppConfigurationProvider
          env={activeEnvironment}
          options={{
            featureFlags,
          }}
          defaults={env}
          axios={Axios}
          isAxiosAuthenticated={authenticated}
          fallback={<Loading />}
          blocking={false}
        >
          <CinchAppConfigProvider ready={axiosReady}>
            {modal && (
              <Modal modal={modal} open={Boolean(modal)} toggle={toggleModal} />
            )}
            <Routes>
              <Route
                path='/multifamilyPortal'
                element={<MultifamilyPortal />}
              />
              <Route path='/login' element={<LoLogin />} />
              <Route path='/logout' element={<Logout />} />
              <Route path='/login' element={<LoLogin />} />
              <Route path='/terms' element={<Terms />} />
              <Route path='/privacy' element={<Privacy />} />
              <Route
                path='/callback'
                element={<Loading label='Logging you in' />}
              />
              <Route
                path='/pop-out/*'
                element={(
                  <AuthenticationGate>
                    <SlowSuspense fallback={<Loading label='' />}>
                      <Routes>
                        <Route
                          path=':loanGuid/efolder/view/:documentGuid/attachments/:attachmentId'
                          element={<EFolderPopOut />}
                        />
                        <Route
                          path=':loanGuid/efolder/view/:documentGuid/attachments'
                          element={<EFolderPopOut />}
                        />
                        <Route
                          path=':loanGuid/efolder/view/:documentGuid'
                          element={<EFolderPopOut />}
                        />
                        <Route
                          path=':loanGuid/efolder/view'
                          element={<EFolderPopOut />}
                        />
                        <Route
                          path=':loanGuid/multifamilydocument/view/:documentGuid/attachments/:attachmentId'
                          element={<MultifamilyPopOut />}
                        />
                        <Route
                          path=':loanGuid/multifamilydocument/view/:documentGuid/attachments'
                          element={<MultifamilyPopOut />}
                        />
                        <Route
                          path=':loanGuid/multifamilydocument/view/:documentGuid'
                          element={<MultifamilyPopOut />}
                        />
                        <Route
                          path=':loanGuid/multifamilydocument/view'
                          element={<MultifamilyPopOut />}
                        />
                      </Routes>
                    </SlowSuspense>
                  </AuthenticationGate>
                )}
              />

              <Route
                path='/*'
                element={(
                  <AuthenticationGate>
                    <WebsocketProvider
                      hub={`${apiServer}ws/hub?loanOfficerId=${loanOfficer?.id}`}
                      options={wsHubOptions}
                      ready={Boolean(authenticated && loanOfficer?.id)}
                    >
                      <ImpersonationBanner />
                      <EllieMaeStatusBanner />
                      <Header />
                      <SlowSuspense fallback={<Loading label='' />}>
                        <ChatLauncher />
                        <Routes>
                          <Route path='admin/*' element={<AdminBase />} />
                          <Route
                            path='compliance/*'
                            element={<ComplianceBase />}
                          />
                          <Route
                            path='approval/:loanId/:userEmail'
                            element={<CommercialExceptionApproval />}
                          />
                          <Route path='*' element={<LOBase />} />
                        </Routes>
                      </SlowSuspense>
                    </WebsocketProvider>
                  </AuthenticationGate>
                )}
              />
              <Route path='*' element={<NotFound />} />
            </Routes>
          </CinchAppConfigProvider>
          {process.env.NODE_ENV === 'development' && (
            <Suspense fallback=''>
              <ServerPicker />
            </Suspense>
          )}
        </AnonymousAppConfigurationProvider>
      </Suspense>
    </FullPageErrorBoundary>
  );
};

export default App;
